/**
 * Agent 详情子页
 * 路径: /dashboard/agent/:id
 * 功能: 显示 Agent 详细信息，包括头像、名称、驱动者信息、统计数据
 *       以及两个 Tab: 自动发布、互动点赞
 */

import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { FeedCard, MasonryGrid } from '../../components/feed'
import NoteDetailModal from '../../components/note/NoteDetailModal'
import ConfirmModal from '../../components/common/ConfirmModal'
import { useAuthStore } from '../../stores/authStore'
import { HeartIcon } from '../../components/common/Icons'
import ExpandableText from '../../components/common/ExpandableText'
import {
  getAgentById,
  getAgentPosts,
  getAgentLikedPosts,
  deleteAgent,
  AgentDetail,
  AgentPost,
  AgentLikedPost,
} from '../../services/agentService'
import { checkFollowStatus, toggleFollow } from '../../services/followService'

// Tab 类型
type AgentTabType = 'posts' | 'likes'

// 触发类型中文映射
const triggerTypeMap: Record<string, string> = {
  'agent_init': 'Initialization',
  'scheduled_post': 'Auto Post',
  'global_explore': 'Global Explore',
  'follow_new_post': 'Follow Feed',
  'received_comment': 'Reply Comment',
  'received_like': 'Received Like',
}

// 格式化数字（如 15400 -> 15.4k）
const formatCount = (count: number): string => {
  if (count >= 10000) return `${(count / 10000).toFixed(1)}w`
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`
  return count.toString()
}

// 格式化下一次活动时间
const formatNextActivity = (nextActiveAt: string | null, nextTriggerType: string | null): string | null => {
  if (!nextActiveAt || !nextTriggerType) return null

  const nextTime = new Date(nextActiveAt)
  const now = new Date()
  const diffMs = nextTime.getTime() - now.getTime()

  // 如果时间已过，显示"待执行"
  if (diffMs <= 0) {
    return `Pending: ${triggerTypeMap[nextTriggerType] || nextTriggerType}`
  }

  // 格式化时间差
  const diffMinutes = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  let timeText = ''
  if (diffDays > 0) {
    timeText = `${diffDays}d later`
  } else if (diffHours > 0) {
    timeText = `${diffHours}h later`
  } else if (diffMinutes > 0) {
    timeText = `${diffMinutes}m later`
  } else {
    timeText = 'Soon'
  }

  const triggerName = triggerTypeMap[nextTriggerType] || nextTriggerType
  return `${timeText}: ${triggerName}`
}

const AgentProfile: React.FC = () => {
  const { id: agentId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()
  const [activeTab, setActiveTab] = useState<AgentTabType>('posts')
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [followLoading, setFollowLoading] = useState(false)

  // 删除确认弹窗状态
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  // 获取 Agent 详情 - 设置refetchOnMount为always确保每次都刷新
  const { data: agent, isLoading: agentLoading, error: agentError, refetch: refetchAgent } = useQuery<AgentDetail | null>({
    queryKey: ['agentDetail', agentId],
    queryFn: async () => {
      if (!agentId) return null
      const { data, error } = await getAgentById(agentId)
      if (error) throw error
      return data
    },
    enabled: !!agentId,
    refetchOnMount: 'always',
  })

  // 当从配置页返回时刷新数据
  useEffect(() => {
    if (location.state?.refresh) {
      refetchAgent()
      // 清除state
      window.history.replaceState({}, '')
    }
  }, [location.state, refetchAgent])

  // 获取 Agent 发布的帖子
  const { data: posts = [], isLoading: postsLoading } = useQuery<AgentPost[]>({
    queryKey: ['agentPosts', agentId],
    queryFn: async () => {
      if (!agentId) return []
      const { data, error } = await getAgentPosts(agentId)
      if (error) throw error
      return data || []
    },
    enabled: !!agentId && activeTab === 'posts',
  })

  // 获取 Agent 点赞的帖子
  const { data: likedPosts = [], isLoading: likesLoading } = useQuery<AgentLikedPost[]>({
    queryKey: ['agentLikedPosts', agentId],
    queryFn: async () => {
      if (!agentId) return []
      const { data, error } = await getAgentLikedPosts(agentId)
      if (error) throw error
      return data || []
    },
    enabled: !!agentId && activeTab === 'likes',
  })

  // 处理卡片点击
  const handleCardClick = (postId: string) => {
    setSelectedPostId(postId)
    setIsModalOpen(true)
  }

  // 关闭弹窗
  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedPostId(null)
  }

  // 返回上一页（支持从不同页面进入）
  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from)
    } else {
      navigate('/dashboard')
    }
  }

  // 跳转配置页
  const handleConfig = () => {
    navigate(`/dashboard/agent/${agentId}/config`)
  }

  // 删除 Agent 处理
  const handleDeleteAgent = async () => {
    if (!agentId || !user?.id) return

    setDeleteLoading(true)
    try {
      const result = await deleteAgent(agentId, user.id)
      if (result.success) {
        setShowDeleteConfirm(false)
        // 跳转回 Dashboard 页面
        navigate('/dashboard', { state: { refresh: true } })
      } else {
        console.error('删除 Agent 失败:', result.error)
      }
    } finally {
      setDeleteLoading(false)
    }
  }

  // 将帖子数据转换为 FeedCard 需要的格式
  const formatPostsForFeed = (postsList: AgentPost[]) => {
    return postsList.map(post => ({
      id: post.id,
      title: post.content || 'No content',
      image: post.images && post.images.length > 0 ? post.images[0] : undefined,
      aspectRatio: '3/4',
      author: { name: post.author_name, avatar: post.author_avatar || '' },
      isAI: true,
      likes: post.likes_count || 0,
    }))
  }

  const formatLikedPostsForFeed = (postsList: AgentLikedPost[]) => {
    return postsList.map(post => ({
      id: post.id,
      title: post.content || 'No content',
      image: post.images && post.images.length > 0 ? post.images[0] : undefined,
      aspectRatio: '3/4',
      author: { name: post.author_name, avatar: post.author_avatar || '' },
      isAI: post.author_type === 'agent',
      likes: post.likes_count || 0,
    }))
  }

  const currentFeedData = activeTab === 'posts'
    ? formatPostsForFeed(posts)
    : formatLikedPostsForFeed(likedPosts)

  const isFeedLoading = activeTab === 'posts' ? postsLoading : likesLoading

  // 是否是 Agent 的驱动者（只有驱动者可以配置）
  const isOwner = agent?.owner_id === user?.id

  // 查询关注状态（仅当不是 owner 且已登录时）
  const { data: isFollowing = false, refetch: refetchFollowStatus } = useQuery<boolean>({
    queryKey: ['followStatus', user?.id, agentId, 'agent'],
    queryFn: async () => {
      if (!user?.id || !agentId) return false
      return checkFollowStatus(user.id, agentId, 'agent')
    },
    enabled: !!user?.id && !!agentId && !isOwner,
  })

  // 关注/取消关注处理
  const handleFollowToggle = async () => {
    if (!user?.id || !agentId || followLoading) return

    setFollowLoading(true)
    try {
      const result = await toggleFollow(user.id, agentId, 'agent', isFollowing)
      if (result) {
        // 刷新关注状态
        await refetchFollowStatus()
        // 刷新 Agent 详情以更新粉丝数
        await refetchAgent()
      }
    } catch (error) {
      console.error('关注操作失败:', error)
    } finally {
      setFollowLoading(false)
    }
  }

  // 加载状态
  if (agentLoading) {
    return (
      <div className="flex-1 flex flex-col min-w-0 bg-[#F8F8F9] min-h-screen">
        <header className="sticky top-0 z-[90] flex items-center h-[60px] md:h-[72px] bg-white/98 border-b border-[#EBEBEB] px-4 md:px-10">
          <button onClick={handleBack} className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors text-[#333333]">
            <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
          </button>
        </header>
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-2 border-[#FF2442] border-t-transparent rounded-full" />
        </div>
      </div>
    )
  }

  // 错误状态
  if (agentError || !agent) {
    return (
      <div className="flex-1 flex flex-col min-w-0 bg-[#F8F8F9] min-h-screen">
        <header className="sticky top-0 z-[90] flex items-center h-[60px] md:h-[72px] bg-white/98 border-b border-[#EBEBEB] px-4 md:px-10">
          <button onClick={handleBack} className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors text-[#333333]">
            <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
          </button>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center px-4">
          <p className="text-[#999999] text-[14px] mb-4">Agent does not exist or failed to load</p>
          <button
            onClick={handleBack}
            className="px-6 py-2 bg-[#FF2442] text-white rounded-full text-[14px] font-medium"
          >
            Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col min-w-0 bg-[#F8F8F9] min-h-screen">
      {/* 顶部导航 */}
      <header className="sticky top-0 z-[90] flex items-center justify-between h-[60px] md:h-[72px] bg-white/98 border-b border-[#EBEBEB] px-4 md:px-10">
        {/* 返回按钮 */}
        <button onClick={handleBack} className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors text-[#333333]">
          <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </button>

        {/* 右侧按钮区域 */}
        <div className="flex items-center gap-2">
          {/* 配置按钮 - 仅驱动者可见 */}
          {isOwner && (
            <button
              onClick={handleConfig}
              className="flex items-center gap-2 px-5 py-2 border border-[#EBEBEB] rounded-[20px] text-[14px] font-medium text-[#333333] hover:bg-[#F5F5F7] transition-all"
            >
              <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.06-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.73,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.06,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 C9.64,21.83,9.83,22,10.08,22h3.84c0.24,0,0.43-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.49-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z" />
              </svg>
              Configure
            </button>
          )}

          {/* 删除按钮 - 仅驱动者可见 */}
          {isOwner && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 px-5 py-2 border border-[#EBEBEB] rounded-[20px] text-[14px] font-medium text-[#FF2442] hover:bg-red-50 transition-all"
            >
              <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
              </svg>
              Delete
            </button>
          )}

          {/* 关注按钮 - 非驱动者且已登录时显示 */}
          {!isOwner && user && (
            <button
              onClick={handleFollowToggle}
              disabled={followLoading}
              className={`flex items-center gap-2 px-5 py-2 rounded-[20px] text-[14px] font-medium transition-all ${
                isFollowing
                  ? 'border border-[#EBEBEB] text-[#333333] hover:bg-[#F5F5F7]'
                  : 'bg-[#FF2442] text-white hover:bg-[#E02040]'
              } ${followLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {followLoading ? (
                <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
              ) : isFollowing ? (
                'Following'
              ) : (
                'Follow'
              )}
            </button>
          )}
        </div>
      </header>

      {/* 核心内容区 */}
      <div className="px-3 md:px-10 pb-6 md:pb-10 max-w-[900px] mx-auto w-full">
        {/* Profile Header */}
        <div className="flex flex-col md:flex-row gap-4 md:gap-8 py-6 md:py-12 justify-center items-center md:items-start text-center md:text-left">
          {/* 头像 */}
          <div className="w-[100px] h-[100px] md:w-[160px] md:h-[160px] rounded-full bg-[#F5F5F7] border border-[#EBEBEB] shadow-sm flex-shrink-0 flex items-center justify-center text-[40px] md:text-[64px] overflow-hidden">
            {agent.avatar_url ? (
              <img
                src={agent.avatar_url}
                alt={agent.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-[#999999]">{agent.name.charAt(0)}</span>
            )}
          </div>

          {/* 信息区域 */}
          <div className="flex flex-col max-w-[500px] flex-1">
            {/* 名称行 */}
            <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
              <h1 className="text-[24px] md:text-[32px] font-semibold text-[#333333]">
                {agent.name}
              </h1>
              {/* AI Agent 标签 */}
              <span className="text-[12px] px-2 py-0.5 rounded-[6px] font-semibold bg-gradient-to-br from-[#FF5B71] to-[#FF2442] text-white">
                AI Agent
              </span>
            </div>

            {/* 驱动者信息 */}
            <div className="flex flex-wrap justify-center md:justify-start gap-2 md:gap-4 mb-4 text-[13px] text-[#999999]">
              <span className="bg-[#F5F5F7] px-3 py-1 rounded-[12px]">
                Creator: @{agent.owner_name}
              </span>
            </div>

            {/* 描述/人格设定 */}
            <ExpandableText
              text={agent.bio || agent.persona}
              maxLines={3}
              className="text-[14px] text-[#333333] mb-4"
            />
            {/* 兴趣标签 */}
            {agent.interest_tags && agent.interest_tags.length > 0 && (
              <div className="text-[13px] text-[#999999] mb-6">
                Tags: {agent.interest_tags.join(', ')}
              </div>
            )}

            {/* 统计数据 */}
            <div className="flex justify-center md:justify-start gap-6 md:gap-8 text-[14px] text-[#999999]">
              <div>
                <span className="font-semibold text-[#333333] text-[16px] mr-1">
                  {formatCount(agent.following_count || 0)}
                </span>
                Following
              </div>
              <div>
                <span className="font-semibold text-[#333333] text-[16px] mr-1">
                  {formatCount(agent.followers_count || 0)}
                </span>
                Followers
              </div>
              <div>
                <span className="font-semibold text-[#333333] text-[16px] mr-1">
                  {formatCount(agent.likes_received_count || 0)}
                </span>
                Likes
              </div>
            </div>

            {/* 下一次活动信息 */}
            {agent.status === 'active' && formatNextActivity(agent.next_active_at, agent.next_trigger_type) && (
              <div className="mt-4 flex justify-center md:justify-start">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F5F5F7] text-[13px] text-[#666666]">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"/>
                  </svg>
                  <span>{formatNextActivity(agent.next_active_at, agent.next_trigger_type)}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tab 切换 */}
        <div className="flex justify-center mb-6">
          <div className="flex bg-black/3 rounded-full p-1 gap-1">
            <div
              className={`py-2 px-6 md:px-8 text-[14px] md:text-[15px] cursor-pointer rounded-full transition-all font-medium ${
                activeTab === 'posts'
                  ? 'bg-white text-[#333333] font-semibold shadow-sm'
                  : 'text-[#999999] hover:text-[#333333]'
              }`}
              onClick={() => setActiveTab('posts')}
            >
              Auto Posts
            </div>
            <div
              className={`py-2 px-6 md:px-8 text-[14px] md:text-[15px] cursor-pointer rounded-full transition-all font-medium flex items-center gap-1.5 ${
                activeTab === 'likes'
                  ? 'bg-white text-[#333333] font-semibold shadow-sm'
                  : 'text-[#999999] hover:text-[#333333]'
              }`}
              onClick={() => setActiveTab('likes')}
            >
              <HeartIcon size={16} />
              Liked Posts
            </div>
          </div>
        </div>

        {/* 瀑布流内容 */}
        {isFeedLoading ? (
          <div className="flex justify-center items-center py-20 text-[#999999]">
            <div className="animate-spin w-6 h-6 border-2 border-[#FF2442] border-t-transparent rounded-full mr-3" />
            Loading...
          </div>
        ) : currentFeedData.length > 0 ? (
          <MasonryGrid
            gap={16}
            useScreenWidth={true}
            responsiveColumns={{
              desktop: 4,  // > 1600px
              laptop: 3,   // 1200px - 1600px
              tablet: 3,   // 900px - 1200px
              mobile: 2,   // < 900px
            }}
          >
            {currentFeedData.map((item) => (
              <FeedCard
                key={item.id}
                id={item.id}
                title={item.title}
                image={item.image}
                aspectRatio={item.aspectRatio}
                author={item.author}
                isAI={item.isAI}
                likes={item.likes}
                onClick={() => handleCardClick(item.id)}
              />
            ))}
          </MasonryGrid>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-[#999999]">
            {activeTab === 'posts' ? (
              <p>No posts yet</p>
            ) : (
              <p>No liked posts yet</p>
            )}
          </div>
        )}
      </div>

      {/* 笔记详情弹窗 */}
      <NoteDetailModal
        postId={selectedPostId}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />

      {/* 删除确认弹窗 */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Agent"
        message="Are you sure you want to delete this Agent? All its posts, comments, and data will be permanently removed. This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        danger={true}
        loading={deleteLoading}
        onConfirm={handleDeleteAgent}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  )
}

export default AgentProfile